import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Mailbox } from 'lucide-react';
import dateFnsLocales from '@utils/dateFnsLocales';
import { useBranding } from '@utils/api';
import { Service } from '@utils/api/aboboApiTypes';
import { useBookingContext } from '@contexts/BookingContext';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { TriangleAlert } from 'lucide-react';

type CompletionStepProps = {
  selectedService?: Service;
};

const CompletionStep: FC<CompletionStepProps> = ({ selectedService }) => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const { bookingData, isSubmitting, submissionError, retrySubmission } = useBookingContext();

  if (!branding) return null;

  // Show loading state while submitting
  if (isSubmitting) {
    return (
      <div className="w-3/4 mx-auto">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: branding.accentColor }}></div>
          <p className="text-lg font-medium">{t('booking.submitting', { defaultValue: 'Submitting your request...' })}</p>
          <p className="text-sm text-gray-600">{t('booking.submittingMessage', { defaultValue: 'Please wait while we process your appointment request.' })}</p>
        </div>
      </div>
    );
  }

  // Show error state if submission failed
  if (submissionError) {
    return (
      <div className="w-3/4 mx-auto">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="text-red-500 text-xl"><TriangleAlert /></div>
          <p className="text-lg font-medium text-red-600">{t('booking.submissionError', { defaultValue: 'Submission Failed' })}</p>
          <p className="text-sm text-gray-600 text-center">{submissionError}</p>
          <Button 
            onClick={retrySubmission}
            disabled={isSubmitting}
            style={{ backgroundColor: branding.buttonColor }}
            className="hover:shadow-lg transition-all hover:-translate-y-px"
          >
            {t('booking.buttons.retry', { defaultValue: 'Try Again' })}
          </Button>
        </div>
      </div>
    );
  }

  const scheduledAt = bookingData.appointmentsAttributes[0]?.scheduledAt;
  const hasAppointmentTime = selectedService?.appointmentTimeRequired && scheduledAt;

  return (
    <div className="w-3/4 mx-auto">
      <p className="text-lg font-medium p-4">
        {t('success.thanks', {name: bookingData.preferredFirstName || bookingData.firstName})}
      </p>
      <div className="flex justify-center">
        <Mailbox color={branding.accentColor} size={36}/>
      </div>
      <div className="pt-2 pb-4">
        {hasAppointmentTime && (
          <>
            <p className="text-base">{t('success.appointmentTitle')}</p>
            <p className="text-lg font-medium">
              {t('success.appointmentDateTime', {
                day: format(parseISO(scheduledAt), "ccccccc, MMM dd yyyy", { 
                  locale: dateFnsLocales[i18n.language] 
                }), 
                time: format(parseISO(scheduledAt), "h:mm a", { 
                  locale: dateFnsLocales[i18n.language] 
                })
              })}
            </p>
            <p className="text-base">{t('success.appointmentFooter')}</p>
          </>
        )}
        {!hasAppointmentTime && (
          <p>{t('success.appointmentTitleNoApptTime')}</p>
        )}
      </div>
      <Separator/>
      <p className="p-4 text-lg">
        {hasAppointmentTime && 
          t('success.nextSteps', {clinicName: branding.name})
        }
        {!hasAppointmentTime && 
          t('success.nextStepsNoApptTime', {clinicName: branding.name})
        }
      </p>
      <Separator/>
      <p className="p-4 text-sm">{t('success.closePage')}</p>
    </div>
  );
};

export default CompletionStep;
